import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { RegistrationDetail } from 'src/app/types/timeregistration/RegistrationDetail';
import { TimeRegistration } from 'src/app/types/timeregistration/TimeRegistration';
import { environment } from 'src/environments/environment';
import type {
  TimeHourlyRateResponse,
  BillingTypeByIdResponse,
  PostAndProcessResponse,
} from 'src/app/api-client';

const apiUrl = environment.CalystaApiBaseURL;

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  url = apiUrl + 'api/Timeregistration';
  constructor(private http: HttpClient) {}

  getUrl() {
    return this.url;
  }

  processTimeRegistration(registrationIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.url}/Registration/ProcessTime`, {
      registrationIds,
    });
  }

  getHourlyRate(EmployeeId: number, CaseId: number): Observable<number> {
    let params = new HttpParams().set('EmployeeId', EmployeeId);
    if (CaseId != null) {
      params = params.set('CaseId', CaseId);
    }
    return this.http
      .get<TimeHourlyRateResponse>(`${this.url}/HourlyRate`, { params })
      .pipe(map((data) => data.HourlyRate ?? 0));
  }

  getBillingType(id: number): Observable<BillingTypeByIdResponse> {
    return this.http.get<BillingTypeByIdResponse>(`${this.url}/BillingType/${id}`);
  }

  insertDetails(details: any, registrationId: number): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const params = new HttpParams({
      fromObject: { Values: JSON.stringify(details), RegistrationId: registrationId },
    });
    return this.http.post<any>(`${this.url}/Registration/Detail`, params, { headers });
  }

  insertRegistration(registration: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const params = new HttpParams({ fromObject: { Values: JSON.stringify(registration) } });
    return this.http.post<any>(`${this.url}/Registration`, params, { headers });
  }

  getRegistrationDetails(registrationId: number): Observable<RegistrationDetail[]> {
    return this.http
      .get<{ data: RegistrationDetail[] }>(`${this.url}/Registration/Detail/${registrationId}`)
      .pipe(map((res) => res.data ?? []));
  }

  getRegistrationDetail(id: number): Observable<TimeRegistration> {
    const params = new HttpParams().set('RegistrationDetailId', id.toString());
    return this.http.get<TimeRegistration>(`${this.url}/Registration/Detail/GetDetail`, { params }).pipe(
      tap(data => console.log('Raw result from API:', data)),
      map(result => ({
        ...result,
        WorkedOn: this.parseSafeDate(result.WorkedOn),
        WorkedOnEnded: this.parseSafeDate(result.WorkedOnEnded),
      }))
    );
  }

  getRegistration(showProcessed: boolean): Observable<any> {
    return this.http.get<any>(
      `${this.url}/Registration/Mine?ShowProcessed=${showProcessed}&RequireTotalCount=true`
    );
  }

  saveAndProcessTime(TimeRegistration: TimeRegistration & { DraftId?: string | null; ReviewSeconds?: number | null }): Observable<PostAndProcessResponse> {
    return this.http.post<PostAndProcessResponse>(`${this.url}/Registration/PostAndProcess`, TimeRegistration);
  }

  updateAndProcessTime(TimeRegistration: TimeRegistration): Observable<PostAndProcessResponse> {
    return this.http.put<PostAndProcessResponse>(`${this.url}/Registration/PutAndProcess`, TimeRegistration);
  }

  withdrawTimeRegistration(registrationDetailId: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/Registration/WithdrawTime`, {
      params: new HttpParams().set('RegistrationDetailId', registrationDetailId.toString()),
    });
  }

  parseSafeDate = (val: any): Date | null => {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };
}
